/**
 * Maps CMM order status to the table/footer CTA button configuration.
 */
import { OrderStatus } from "../../../enums/authStatus";

/**
 * CTA button configuration for a CMM order status.
 */
interface ButtonConfig {
  label: string;
  buttonId: string;
  disabled: boolean;
}

/**
 * Returns the CTA label, action id, and enabled state for a CMM status.
 *
 * @param cmmStatus - Current CMM order status value
 * @param hasMissingData - Whether the order has missing required fields
 * @returns Button label, id, and disabled flag for the status
 */
export const getButtonConfig = (
  cmmStatus?: string,
  hasMissingData?: boolean,
): ButtonConfig => {
  switch (cmmStatus) {
    case OrderStatus.IN_PROGRESS:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: true,
      };
    case OrderStatus.INACCURACY:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: true,
      };
    case OrderStatus.PRESCRIPTION_ERROR:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: true,
      };
    case OrderStatus.INSURANCE_ERROR:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: true,
      };
    case OrderStatus.FORM_ERROR:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: true,
      };
    case OrderStatus.FORM_FILLED:
      // If there's missing data (Yes), disable the button
      // If no missing data (No), enable the button
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: hasMissingData === true,
      };
    case OrderStatus.SENDING_TO_PLAN:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: true,
      };
    case OrderStatus.SENDING_TO_PLAN_ERROR:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: false,
      };
    case OrderStatus.SENT_TO_PLAN:
      return {
        label: "Fetch QA",
        buttonId: "fetch_qa",
        disabled: false,
      };
    case OrderStatus.QA_IN_PROGRESS:
      return {
        label: "Fetch QA",
        buttonId: "fetch_qa",
        disabled: true,
      };
    case OrderStatus.QA_NOT_FOUND:
      return {
        label: "Retry QA",
        buttonId: "retry_qa",
        disabled: false,
      };
    case OrderStatus.QA_ERROR:
      return {
        label: "Retry QA",
        buttonId: "retry_qa",
        disabled: false,
      };
    case OrderStatus.QA_INCOMPLETE:
      return {
        label: "Retry QA",
        buttonId: "retry_qa",
        disabled: false,
      };
    case OrderStatus.QA_FETCHED:
      return {
        label: "View Clinical",
        buttonId: "view_clinical",
        disabled: false,
      };
    case OrderStatus.SEND_TO_PLAN_ERROR:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: false,
      };
    case OrderStatus.FIRST_STP_OUTCOME:
      return {
        label: "View Outcome",
        buttonId: "view_outcome",
        disabled: false,
      };
    case OrderStatus.UPDATING_QA:
      return {
        label: "Retry Update",
        buttonId: "retry_update",
        disabled: true,
      };
    case OrderStatus.QA_UPDATE_ERROR:
      return {
        label: "Retry Update",
        buttonId: "retry_update",
        disabled: false,
      };
    case OrderStatus.QA_UPDATED:
      return {
        label: "Submit PA",
        buttonId: "submit_pa",
        disabled: false,
      };
    case OrderStatus.SUBMITTING_PA:
      return {
        label: "Retry Submit",
        buttonId: "retry_submit",
        disabled: true,
      };
    case OrderStatus.SUBMISSION_ERROR:
      return {
        label: "Retry Submit",
        buttonId: "retry_submit",
        disabled: false,
      };
    case OrderStatus.PA_SUBMITTED:
      return {
        label: "View Outcome",
        buttonId: "view_outcome",
        disabled: false,
      };
    case OrderStatus.NOT_STARTED:
      return {
        label: "Run Case",
        buttonId: "run_case",
        disabled: true,
      };
    default:
      return {
        label: "Send to Plan",
        buttonId: "send_to_plan",
        disabled: true,
      };
  }
};

/**
 * Statuses whose CMM orders table CTA is collapsed into a single
 * "View Clinical" button that opens the questionnaire page.
 * The in-page (footer) CTAs keep their status-specific actions.
 */
const VIEW_CLINICAL_TABLE_STATUSES: string[] = [
  OrderStatus.UPDATING_QA,
  OrderStatus.QA_UPDATE_ERROR,
  OrderStatus.QA_UPDATED,
  OrderStatus.SUBMITTING_PA,
  OrderStatus.SUBMISSION_ERROR,
  OrderStatus.PA_SUBMITTED,
];

/**
 * Returns the CTA config for the CMM orders table Action column.
 *
 * Post-QA statuses show "View Clinical" (navigates to the questionnaire);
 * every other status falls back to the shared status-based config.
 *
 * @param cmmStatus - Current CMM order status value
 * @param hasMissingData - Whether the order has missing required fields
 * @returns Button label, id, and disabled flag for the table row
 */
export const getTableButtonConfig = (
  cmmStatus?: string,
  hasMissingData?: boolean,
): ButtonConfig => {
  if (cmmStatus && VIEW_CLINICAL_TABLE_STATUSES.includes(cmmStatus)) {
    return {
      label: "View Clinical",
      buttonId: "view_clinical",
      disabled: false,
    };
  }
  return getButtonConfig(cmmStatus, hasMissingData);
};
